import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles';
import {
  MapFiltersContainer,
  MapFiltersBody,
  FilterToggleButton,
  ClearFiltersButton,
} from './MapFilters.css';
import { theme as localTheme } from '../../styles';

const useStyles = makeStyles((theme) => ({
  formControl: {
    minWidth: 'auto',
    margin: `${theme.spacing(1)}px 0 ${theme.spacing(1)}px ${theme.spacing(
      1
    )}px`,
    [theme.breakpoints.up('sm')]: {
      minWidth: 180,
    },
  },
  selectEmpty: {
    color: localTheme.colors.primaryred,
    border: `1.5px solid ${localTheme.colors.primaryred}`,
  },
  isOpen: {
    color: '#000000',
    background: '#ffffff',
  },
}));

const theme = createMuiTheme({
  overrides: {
    // Style sheet name
    MuiSelect: {
      // Name of the rule
      select: {
        paddingLeft: '5px',
        textTransform: 'uppercase',
      },
      icon: {
        // Some CSS
        color: localTheme.colors.primaryred,
        '&:hover': {
          color: '#000000',
        },
      },
    },
    MuiListItem: {
      button: {
        borderBottom: '1px solid black',
        borderLeft: '1px solid black',
        borderRight: '1px solid black',
        textTransform: 'uppercase',
        '&:first-of-type': {
          borderTop: '1px solid black',
        },
        '&.Mui-selected': {
          backgroundColor: localTheme.colors.primaryred,
          color: '#ffffff',
          borderColor: 'black',
          '&:hover': {
            backgroundColor: localTheme.colors.primaryred,
            color: '#ffffff',
            borderColor: 'black',
          },
        },
        '&:hover': {
          backgroundColor: localTheme.colors.primaryred,
          color: '#ffffff',
        },
      },
    },
    MuiMenu: {
      list: {
        padding: 0,
      },
    },
    MuiPaper: {
      rounded: {
        borderRadius: 0,
      },
    },
    MuiInput: {
      underline: {
        '&::after': {
          borderBottomColor: localTheme.colors.primaryred,
        },
      },
    },
  },
});

const MapFilters = ({
  selectedCountry,
  setSelectedCountry,
  query,
  setSelectedService,
  selectedService,
  lang,
  filterMode,
  activateFilterMode,
  activateTimelineMode,
  allProjects,
}) => {
  const classes = useStyles();
  const [countrySelectOpen, setCountrySelectOpen] = useState(false);
  const [serviceSelectOpen, setServiceSelectOpen] = useState(false);
  const flagsSelector = query.projects.flags_selector;

  // Calculate most recent year from projects
  const mostRecentYear = useMemo(() => {
    if (!allProjects || allProjects.length === 0) return 2026;
    let maxYear = 2026;
    for (let i = 0; i < allProjects.length; i++) {
      const node = allProjects[i]?.node;
      if (node?.data?.year) {
        const year = parseInt(node.data.year, 10);
        if (year > maxYear) maxYear = year;
      }
    }
    return maxYear;
  }, [allProjects]);

  const handleFilterButtonClick = () => {
    activateFilterMode(mostRecentYear);
  };

  const handleClearFilters = () => {
    activateTimelineMode();
  };

  useEffect(() => {
    const isEducation = selectedService.toLowerCase().includes('edu');
    const isFinance = selectedService.toLowerCase().includes('finance');
    const isTech = selectedService.toLowerCase().includes('tech');
    const isProject = selectedService.toLowerCase().includes('proje');

    if (isEducation) {
      setSelectedService(
        query.services.data.service_block.find((s) =>
          s.service_title.text.toLowerCase().includes('edu')
        ).service_title.text
      );
    } else if (isFinance) {
      setSelectedService(
        query.services.data.service_block.find((s) =>
          s.service_title.text.toLowerCase().includes('finance')
        ).service_title.text
      );
    } else if (isTech) {
      setSelectedService(
        query.services.data.service_block.find((s) =>
          s.service_title.text.toLowerCase().includes('tech')
        ).service_title.text
      );
    } else if (isProject) {
      setSelectedService(
        query.services.data.service_block.find((s) =>
          s.service_title.text.toLowerCase().includes('proje')
        ).service_title.text
      );
    }
  }, [lang.locale]);

  return (
    <MapFiltersContainer className={filterMode === 'filters' ? 'filter-mode' : ''}>
      {/* Timeline mode: show "Filter by Country or Service" button */}
      {filterMode === 'timeline' && (
        <FilterToggleButton onClick={handleFilterButtonClick}>
          {query.projects.map_label_filter?.text || 'FILTER BY COUNTRY OR SERVICE'}
        </FilterToggleButton>
      )}

      {/* Filter mode: show dropdowns and clear button */}
      {filterMode === 'filters' && (
        <>
          <MapFiltersBody>
            <ThemeProvider theme={theme}>
              {/* By Country */}
              <FormControl className={classes.formControl}>
                <Select
                  value={selectedCountry}
                  onChange={(e) => {
                    setSelectedService('');
                    setSelectedCountry(e.target.value);
                  }}
                  open={countrySelectOpen}
                  onClose={() => setCountrySelectOpen(false)}
                  onOpen={() => setCountrySelectOpen(true)}
                  displayEmpty
                  className={`${classes.selectEmpty} ${
                    countrySelectOpen ? classes.isOpen : ''
                  }`}
                  inputProps={{ 'aria-label': 'Without label' }}
                >
                  {query.projects.map_label_country ? (
                    <MenuItem value=''>
                      {query.projects.map_label_country.text}
                    </MenuItem>
                  ) : (
                    ''
                  )}
                  {flagsSelector?.length > 0
                    ? flagsSelector.map((flag, index) => {
                        return (
                          <MenuItem
                            key={`${flag?.country}--${index}`}
                            value={flag?.country}
                          >
                            {flag?.country}
                          </MenuItem>
                        );
                      })
                    : ''}
                </Select>
              </FormControl>
              {/* By Service */}
              <FormControl className={classes.formControl}>
                <Select
                  value={selectedService.trim()}
                  onChange={(e) => {
                    setSelectedCountry('');
                    setSelectedService(e.target.value);
                  }}
                  displayEmpty
                  className={`${classes.selectEmpty} ${
                    serviceSelectOpen ? classes.isOpen : ''
                  }`}
                  inputProps={{ 'aria-label': 'Without label' }}
                  open={serviceSelectOpen}
                  onClose={() => setServiceSelectOpen(false)}
                  onOpen={() => setServiceSelectOpen(true)}
                >
                  {query.projects.map_label_service ? (
                    <MenuItem value=''>
                      {query.projects.map_label_service.text}
                    </MenuItem>
                  ) : (
                    ''
                  )}
                  {query?.services?.data?.service_block
                    ? query.services.data.service_block.map((service, index) => {
                        return (
                          <MenuItem
                            key={`${service.service_title.text}--${index}`}
                            value={service.service_title.text}
                          >
                            {service.service_title.text}
                          </MenuItem>
                        );
                      })
                    : ''}
                </Select>
              </FormControl>
              <ClearFiltersButton onClick={handleClearFilters} aria-label="Clear filters">
                ✕
              </ClearFiltersButton>
            </ThemeProvider>
          </MapFiltersBody>
        </>
      )}
    </MapFiltersContainer>
  );
};

MapFilters.propTypes = {
  setSelectedCountry: PropTypes.func.isRequired,
  query: PropTypes.object.isRequired,
};

export default MapFilters;
